import {
  faUser,
  faUserCheck,
  faUserTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IconButton from "components/common/IconButton";
import { useAppContext } from "providers/AppProvider";
import React, { useMemo, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { Gear, Pen } from "react-bootstrap-icons";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import EditEmpolyeeModal from "./EditEmpolyeeModal";
import { deleteRow, updateRowData } from "../config/empolyeeSlice";

const iconMap = {
  total: faUser,
  valid: faUserCheck,
  invalid: faUserTimes,
};
const SummaryCard = ({ type, title, value, variant, onClick, active }) => {
  const getBgColor = () => {
    if (active) return `bg-${variant}-subtle`;
    return "var(--falcon-quaternary-bg";
  };

  const getHoverColor = () => {
    return `hover-bg-${variant}-subtle`;
  };

  return (
    <Card
      className={`mb-3 summary-card shadow-sm border ${
        active ? `border-${variant} border-2` : `border-${variant}`
      } ${getBgColor()} ${getHoverColor()}`}
      style={{
        cursor: "pointer",
        transition: "all 0.3s ease",
      }}
      onClick={onClick}
    >
      <Card.Body className="d-flex align-items-center justify-content-between p-3">
        <div>
          <h6 className="mb-1 text-muted">{title}</h6>
          <h2 className={`mb-0 fw-bold text-${variant}`}>{value}</h2>
        </div>
        <FontAwesomeIcon
          icon={iconMap[type]}
          className={`text-${variant} fs-2`}
        />
      </Card.Body>
    </Card>
  );
};

const StatusBadge = ({ isValid }) => (
  <Badge bg={isValid ? "success" : "danger"} className="py-2 px-3 rounded-pill">
    {isValid ? "Valid" : "Invalid"}
  </Badge>
);

const EmpolyeeCheckingStep = () => {
  const dispatch = useDispatch();
  const { tableData } = useSelector((state) => state.empolyee);

  const [filterType, setFilterType] = useState("all");
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const {
    config: { theme },
  } = useAppContext();
  const summaryMetrics = useMemo(() => {
    const totalRecords = tableData?.length || 0;
    const validRecords = tableData?.filter((row) => row?.isValid).length || 0;
    const invalidRecords = totalRecords - validRecords || 0;

    return {
      totalRecords,
      validRecords,
      invalidRecords,
      validityPercentage:
        totalRecords > 0 ? Math.round((validRecords / totalRecords) * 100) : 0,
    };
  }, [tableData]);

  const filteredData = useMemo(() => {
    if (filterType === "valid") return tableData?.filter((row) => row?.isValid);
    if (filterType === "invalid")
      return tableData?.filter((row) => !row?.isValid);
    return tableData;
  }, [tableData, filterType]);
  const handleEdit = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const handleDelete = (row) => {
    dispatch(deleteRow(row?.rowNum));
  };
  // Update row validation status after editing
  const updateRowDataRow = (rowNum, updatedData, isValid) => {
    dispatch(updateRowData({ rowNum, updatedData, isValid }));
  };

  const columns = [
    {
      name: "#",
      selector: (row) => row.rowNum - 1,
      width: "60px",
    },
    {
      name: "Status",
      cell: (row) => <StatusBadge isValid={row.isValid} />,
      width: "100px",
    },

    {
      name: "Name",
      sortable: false,
      selector: (row) =>
        `${row?.["First Name (English)"] || row?.first_name_eng || ""} ${
          row?.["Family Name (English)"] || row?.last_name_eng || ""
        }`,
    },
    {
      name: "Nationality",
      sortable: false,
      selector: (row) =>
        `${row?.["Nationality"] || row?.nationality?.title || ""}` || "",
    },
    {
      name: "Email",
      sortable: false,
      selector: (row) => `${row?.["Email"] || row?.email || ""}`,
    },
    {
      name: "System Privilage",
      sortable: false,
      selector: (row) =>
        `${
          row?.["System Privilage"] || row?.system_privilage?.syspri_name || ""
        }`,
    },
    {
      name: <Gear />,
      button: true,
      cell: (row) => (
        <div className="d-flex gap-2">
          <IconButton
            size="sm"
            variant="outline-warning"
            className="mr-2"
            onClick={() => handleEdit(row)}
          >
            <Pen />
          </IconButton>
          <IconButton
            size="sm"
            variant="outline-danger"
            onClick={() => handleDelete(row)}
          >
            <FontAwesomeIcon icon="trash" />
          </IconButton>
        </div>
      ),
      width: "150px",
    },
  ];

  const ExpandableRow = ({ data }) => (
    <div className="p-3">
      {data.errors && data.errors.length > 0 ? (
        <ul className="mb-0 ps-3 text-danger small">
          {data.errors.map((err, idx) => (
            <li key={idx}>{err}</li>
          ))}
        </ul>
      ) : (
        <p className="text-success mb-0">No errors. This row is valid.</p>
      )}
    </div>
  );

  return (
    <div className="mt-4">
      <Row className="g-3 mb-4">
        <Col md={4}>
          <SummaryCard
            type="total"
            title="Total Records"
            value={summaryMetrics.totalRecords}
            variant="primary"
            onClick={() => setFilterType("all")}
            active={filterType === "all"}
          />
        </Col>
        <Col md={4}>
          <SummaryCard
            type="valid"
            title="Valid Records"
            value={summaryMetrics.validRecords}
            variant="success"
            onClick={() => setFilterType("valid")}
            active={filterType === "valid"}
          />
        </Col>
        <Col md={4}>
          <SummaryCard
            type="invalid"
            title="Invalid Records"
            value={summaryMetrics.invalidRecords}
            variant="danger"
            onClick={() => setFilterType("invalid")}
            active={filterType === "invalid"}
          />
        </Col>
      </Row>

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        expandableRows
        theme={theme}
        expandableRowsComponent={ExpandableRow}
        expandableRowDisabled={(row) =>
          !Array.isArray(row.errors) || row.errors.length === 0
        }
        highlightOnHover
        dense
      />
      <EditEmpolyeeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        rowData={selectedRow}
        setSelectedRow={setSelectedRow}
        updateRowData={updateRowDataRow}
      />
    </div>
  );
};

export default EmpolyeeCheckingStep;
